import { useAppMutation } from '@/helpers/api/mutation'

import { createInstance } from '@/helpers/api/base'
import { useModal } from '@/components/Modal'
import {
    IOfficialReport,
    OfficialReportRequestBody,
    OfficialReportStatus,
    OfficialReportType,
} from '@/types/officialReport'
import { useDateStore } from '@/components/DateInput'
import { useNotification } from '@/ui/Notification'
import { useInvalidateControllerLogsByDate } from '@/modules/CombinedLogs/api/useGetControllerLogs'
import { Log } from '@/types/logs'
import { buildOfficialReportRequestBody } from '../helpers/buildOfficialReportRequestBody'
import { FormValuesType } from '../schema'
import { useGetControllerLogParams } from '@/modules/CombinedLogs/apiHelpers/useGetControllerLogParams'
import { formatOfficialReportRequestBody } from '../helpers/formatOfficialReportRequstBody'
import { useGetUserInfo } from '@/modules/Auth'

interface Props {
    log: Log
    type: OfficialReportType
    callback?: (body: OfficialReportRequestBody) => void
}

export const useSendOfficialReport = ({ log, type, callback }: Props) => {
    const { date } = useDateStore()
    const invalidateControllerLogsByDate = useInvalidateControllerLogsByDate()
    const { closeModal } = useModal()
    const { showNotification } = useNotification()

    const logParams = useGetControllerLogParams()
    const { data: userInfo } = useGetUserInfo()

    const logQueryKey = ['controllerLogs', ...logParams]
    const optimisticChangesCallback = (requestBody: OfficialReportRequestBody, previousLogs?: Log[]) => {
        const report = formatOfficialReportRequestBody(requestBody, userInfo, log)
        closeModal()

        if (!report) {
            return previousLogs
        }

        return previousLogs?.map((item) => (item.id === report.logId ? { ...item, report } : item))
    }

    const onSuccessCallback = (data: IOfficialReport) => {
        closeModal()
        invalidateControllerLogsByDate(date)

        if (data.status === OfficialReportStatus.Submitted) {
            showNotification({ message: 'Report submitted', type: 'submit' })
        }
    }

    const createOfficialReportMutation = useAppMutation<IOfficialReport, Error, OfficialReportRequestBody, Log[]>({
        mutationFn: (data) =>
            createInstance({
                url: '/official-reports',
                method: 'POST',
                data,
            }),
        alertOnError: true,
        queryKey: logQueryKey,
        optimisticChangesCallback,
        onSuccess: onSuccessCallback,
    })

    const updateOfficialReportMutation = useAppMutation<
        IOfficialReport,
        Error,
        { id: string; data: OfficialReportRequestBody },
        Log[]
    >({
        mutationFn: ({ id, data }) =>
            createInstance({
                url: `/official-reports/${id}`,
                method: 'PUT',
                data,
            }),
        alertOnError: true,
        queryKey: logQueryKey,
        optimisticChangesCallback: ({ data }: { id: string; data: OfficialReportRequestBody }, previousLogs?: Log[]) =>
            optimisticChangesCallback(data, previousLogs),
        onSuccess: onSuccessCallback,
    })

    const handleSendOfficialReport = async (status: OfficialReportStatus, values: FormValuesType) => {
        const requestBody = buildOfficialReportRequestBody({
            type,
            status,
            log,
            values,
        })

        if (callback) {
            callback(requestBody)
            closeModal()
            return
        }

        if (log.report) {
            updateOfficialReportMutation.mutate({ id: log.report.id, data: requestBody })
        } else {
            createOfficialReportMutation.mutate(requestBody)
        }
    }

    return {
        handleSendOfficialReport,
        isPending: createOfficialReportMutation.isPending || updateOfficialReportMutation.isPending,
    }
}
